import { IPromise } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { StateService } from "@uirouter/angularjs";
import { takeUntil } from "rxjs";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { IStateInit } from "@gtmhub/core/routing";
import { IIndicator, IUIError } from "@gtmhub/error-handling";
import { IMetricMethodologySettingsUI, OkrMethodologyFacade } from "@gtmhub/goals/components/facades/okr-methodology.facade";
import { IGoal } from "@gtmhub/goals/models";
import { GoalService } from "@gtmhub/goals/services/goal.service";
import { IInsightSelectorData } from "@gtmhub/insights";
import { IGtmhubRootScopeService, RequireAtLeastOne } from "@gtmhub/models";
import { OkrEvents } from "@gtmhub/okrs/events";
import { MetricDeadLine, MetricDeadLineType, disableDatesOutsideSessionTimeframe, metricFormStrings, setMetricDueDates } from "@gtmhub/okrs/metrics/utils";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { Ng1DestroyService } from "@gtmhub/shared/services";
import { INgRedux } from "@gtmhub/state-management/models";
import { PermissionsService } from "@gtmhub/users/permissions-service";
import { CfMap, ICustomField } from "@webapp/custom-fields/models/custom-fields.models";
import { filterCustomFieldsBySession } from "@webapp/custom-fields/utils/custom-fields-filter-util";
import { Milestone } from "@webapp/okrs/metrics/components/metric-milestone/metric-milestone.models";
import { ICheckin, Metric, MetricFormEventType, MetricTargetOperator } from "@webapp/okrs/metrics/models/metric.models";
import { MetricsFacade } from "@webapp/okrs/metrics/services/metrics-facade.service";
import { filterWorkflowSelectionProperties, generateInsightSelectorData, isFieldPartOfWorkflowUtil, parseDecimalInput } from "@webapp/okrs/utils/metric.utils";
import { IUserItemAllowedActions, Session } from "@webapp/sessions/models/sessions.model";
import { PeopleSelectorRequest } from "@webapp/shared/components/people-selector/models/models";
import { EditInsightService } from "@webapp/shared/insights/edit-modal/edit-insight.service";
import { MetricService } from "../services/metric.service";

export interface IEditDynamicMetricParams {
  metricId: string;
  metric?: Metric;
  id?: string;
}

interface IEditDynamicMetricReduxScope {
  sessionIdMap: Record<string, Session>;
  reduxItemsFetched: boolean;
  reduxItemsError: IUIError;
}

const bindStateToScope = (state: ISessionsStoreState): IEditDynamicMetricReduxScope => {
  return {
    sessionIdMap: state.sessions.map,
    reduxItemsFetched: state.sessions.isFetched,
    reduxItemsError: state.sessions.error,
  };
};

export interface IEditDynamicMetricScope extends IModalScope, IEditDynamicMetricReduxScope, IExtendedRootScopeService {
  isMilestoneOn: boolean;
  milestoneErrorMessageId: string;
  lastMilestone: Milestone;
  session: Session;
  indicators: {
    loading?: IIndicator;
    saving?: IIndicator;
  };
  metric: Metric;
  params: IEditDynamicMetricParams;
  allowedActions: string[];

  dueDatePicker: {
    minDate;
    maxDate;
  };
  dates: {
    dueDate: string;
    softDueDate: string;
  };

  softDueDatePicker: {
    minDate;
    maxDate;
  };
  // use to determine what to do with the variables on save
  // possible values are: "none", "hardDeadline", "hardAndSoftDeadline"
  useDeadline: MetricDeadLine;
  goal: IGoal;
  isLoaded: boolean;
  errorMessage: string;
  errorMessageId: string;
  useCritical: boolean;
  insightSelector: {
    data: IInsightSelectorData;
    readOnly: boolean;
  };
  requiredCfNotEmpty: boolean;
  peopleSelectorRequest: PeopleSelectorRequest;
  methodologySettings: IMetricMethodologySettingsUI;
  onInsightSelectorChange(): void;
  save(): void;
  includeCritical(params: { useCritical: boolean }): void;
  validateInputValues(targetOperator: string, targetValue: number, criticalValue: number, initialValue: number): boolean;
  updateCustomFields(customFields: CfMap): void;
  setRequiredCf(requiredCf: { areFilled: boolean }): void;
  selectMultipleOwners(ids: string[]): void;
  isFieldPartOfWorkflow(filedName: string): boolean;
  searchInsightSuggestions(name: string): void;
  setDeadline(deadline: MetricDeadLine): void;
  updateMetricField(value: RequireAtLeastOne<Metric>): void;
  setDueDate(dueDate: string, type: MetricDeadLineType): void;
  filterBySessionId(customFields: ICustomField[]): ICustomField[];
  isCustomFieldDisabled(customField: ICustomField): boolean;
  closeModal($event?: KeyboardEvent): void;
}

export class EditDynamicMetricCtrl implements IStateInit {
  public static $inject = [
    "$rootScope",
    "$scope",
    "$state",
    "$stateParams",
    "GoalService",
    "MetricService",
    "PermissionsService",
    "$ngRedux",
    "PlanningSessionsActions",
    "OkrMethodologyFacade",
    "EditInsightService",
    "MetricsFacade",
    "AccountService",
  ];

  private okrEvents: OkrEvents;
  constructor(
    private $rootScope: IGtmhubRootScopeService,
    private $scope: IEditDynamicMetricScope,
    private $state: StateService,
    private $stateParams: IEditDynamicMetricParams,
    private goalService: GoalService,
    private metricService: MetricService,
    private permissionsService: PermissionsService,
    private $ngRedux: INgRedux,
    private planningSessionsActions: PlanningSessionsActions,
    private okrMethodologyFacade: OkrMethodologyFacade,
    private editInsightService: EditInsightService,
    private metricsFacade: MetricsFacade,
    private accountService: AccountService
  ) {
    this.$scope.isMilestoneOn = this.accountService.getAccountSetting("milestones");
    this.okrEvents = new OkrEvents(this.$scope);

    this.$scope.params = { ...this.$stateParams };
    this.$scope.isLoaded = true;

    this.$scope.indicators = {};

    this.$scope.dates = {
      dueDate: null,
      softDueDate: null,
    };
    this.$scope.$on("$destroy", this.$ngRedux.connect(bindStateToScope)(this.$scope));
    this.$scope.save = this.save;
    this.$scope.includeCritical = this.includeCritical;
    this.$scope.validateInputValues = this.validateInputValues;
    this.$scope.onInsightSelectorChange = this.onInsightSelectorChange;
    this.$scope.updateCustomFields = this.updateCustomFields;
    this.$scope.setRequiredCf = this.setRequiredCf;
    this.$scope.selectMultipleOwners = this.selectMultipleOwners;
    this.$scope.isFieldPartOfWorkflow = (fieldName) => this.isFieldPartOfWorkflow(fieldName);
    this.$scope.searchInsightSuggestions = this.searchInsightSuggestions;
    this.$scope.setDeadline = this.setDeadline;
    this.$scope.updateMetricField = this.updateMetricField;
    this.$scope.setDueDate = this.setDueDate;
    this.$scope.filterBySessionId = this.filterBySessionId;
    this.$scope.isCustomFieldDisabled = this.isCustomFieldDisabled;
    this.$scope.closeModal = this.closeModal;
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private searchInsightSuggestions = (name: string) => {
    this.$rootScope.$broadcast("searchInsightSuggestions", { name });
  };

  private init = (): IPromise<unknown> => {
    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
    this.$scope.indicators.loading = { progress: true };

    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.reduxItemsFetched,
        () => this.$scope.reduxItemsError
      )
      .then(
        () => {
          return this.fetchGoalAndMetric().then(
            () => {
              this.getMilestones(this.$scope.metric.id);
              this.setMetricMethodology();
              this.setPeopleSelectorRequest();

              this.$scope.useCritical = !!this.$scope.metric && (this.$scope.metric.critical !== null || this.$scope.metric.critical === 0);

              const newInsightName = this.editInsightService.getNewInsightName();
              if (newInsightName && newInsightName !== this.$scope.metric.insightName) {
                this.$scope.metric.insightName = newInsightName;
              }

              // in a case when user refresh while creating a dynamic KR
              if (!this.$scope.metric) {
                this.$scope.$dismiss();
              }

              const targetId = this.$scope.metric.id ? this.$scope.metric.id : this.$scope.goal.id;
              const targetType = this.$scope.metric.id ? "metric" : "goal";

              this.permissionsService.getCurrentUserAllowedActionsPerItem(targetId, targetType).then(
                (actions: IUserItemAllowedActions) => {
                  delete this.$scope.indicators.loading;
                  this.$scope.allowedActions = actions.currentUserActions;

                  if (this.$scope.goal.sessionId && this.$scope.goal.sessionId !== "") {
                    this.$scope.session = this.$scope.sessionIdMap[this.$scope.goal.sessionId];
                    disableDatesOutsideSessionTimeframe(this.$scope, this.$stateParams);
                  }

                  this.$scope.insightSelector = {
                    data: generateInsightSelectorData(this.$scope.metric),
                    readOnly: !(this.$rootScope.hasPermission(metricFormStrings.manageData) || this.$rootScope.hasRole(metricFormStrings.admin)),
                  };

                  this.$scope.$broadcast("focusTextInput");
                },
                (error) => (this.$scope.indicators.loading = { error })
              );

              this.metricService.trackMetricForm(MetricFormEventType.KR_FORM_OPENED, this.$scope.metric);
            },
            (error) => (this.$scope.indicators.loading = { error })
          );
        },
        () => (this.$scope.indicators.loading = { error: this.$scope.reduxItemsError })
      );
  };

  private fetchGoalAndMetric = (): IPromise<void> => {
    // 1. If we have goalId, we need to fetch the goal, and then we can get the metric from the goal
    // 2. If we don't have goalId, we can fetch the metric and get goalId from the metric, then fetch the goal

    if (this.$stateParams.id) {
      return this.goalService.getGoal(this.$stateParams.id).then((goal) => {
        this.$scope.goal = goal;

        if (this.$scope.params.metricId) {
          this.$scope.metric = this.$scope.goal.metrics.find((metric) => metric.id === this.$scope.params.metricId);
        } else {
          this.$scope.metric = angular.copy(this.$scope.params.metric);
        }
      });
    } else if (this.$stateParams.metricId) {
      return this.metricService.getMetric(this.$stateParams.metricId).then((metric) => {
        this.$scope.metric = metric;
        if (this.$scope.metric.goalId) {
          return this.goalService.getGoal(this.$scope.metric.goalId).then((goal) => {
            this.$scope.goal = goal;
          });
        }
      });
    }
  };

  private getMilestones(metricId: string): void {
    this.metricsFacade
      .getMetric$(metricId, ["milestones"])
      .pipe(takeUntil(new Ng1DestroyService(this.$scope)))
      .subscribe((metric) => {
        this.$scope.metric.milestones = metric.milestones;
      });
  }

  private setMetricMethodology(): void {
    this.$scope.methodologySettings = this.okrMethodologyFacade.getMethodologySettingsUIForMetricView(this.$scope.session);
  }

  private setDeadline = (deadline: MetricDeadLine): void => {
    this.$scope.useDeadline = deadline;
  };

  private isFieldPartOfWorkflow(fieldName: string): boolean {
    return isFieldPartOfWorkflowUtil(fieldName, this.$scope.session);
  }

  private save = () => {
    this.$scope.indicators.saving = { progress: true };

    setMetricDueDates(this.$scope);

    // set metric.critical to null if not dealing with anti-target KRs
    this.setMetricCriticalTarget(this.$scope.metric);

    parseDecimalInput(this.$scope.metric, ["actual", "initialValue", "target"]);

    if (this.$scope.params.metricId) {
      this.updateDynamicMetric();
    } else {
      this.createDynamicMetric();
    }
  };

  private createDynamicMetric = (): void => {
    const shouldCheckin = this.$scope.metric.actual !== this.$scope.metric.initialValue;
    this.metricService.createMetric(this.$scope.metric).then(
      (newMetric: Metric) => {
        if (shouldCheckin) {
          const checkin: ICheckin = {
            checkInDate: new Date().toISOString(),
            actual: this.$scope.metric.actual,
            confidence: this.$scope.metric.confidence?.value,
            customFields: this.$scope.metric.customFields,
          };
          this.metricService.checkin(newMetric, checkin).then(() => {
            this.okrEvents.broadcastGoalsChanged("metricSnapshotCreated", {
              checkin,
              metric: this.$scope.metric,
              goal: this.$scope.goal,
              session: this.$scope.session,
            });
          });
        }

        this.okrEvents.broadcastGoalsChanged("goalChanged");

        delete this.$scope.indicators.saving;

        this.$state.go("^.^");
      },
      (error) => (this.$scope.indicators.saving = { error })
    );
  };

  private updateDynamicMetric = (): void => {
    if (this.$scope.goal.locked && this.$scope.session.workflow?.editableKrFields?.length > 0) {
      const partialMetric: Partial<Metric> = {
        id: this.$scope.metric.id,
      };

      const properties = filterWorkflowSelectionProperties(this.$scope.session.workflow.editableKrFields);

      properties.forEach((property) => {
        partialMetric[property] = this.$scope.metric[property];
      });

      this.patchMetric(partialMetric);
    } else {
      this.patchMetric(this.$scope.metric);
    }
  };

  private patchMetric(metric: Partial<Metric>): void {
    const patch = (metric: Partial<Metric>) => {
      this.metricService.patchMetric(metric).then(
        () => this.handleDynamicMEtricUpdated(),
        (error) => (this.$scope.indicators.saving = { error })
      );
    };

    // GVS-19943
    const shouldCheckin = metric.initialValue !== undefined && metric.initialValue !== this.$scope.metric.actual && !this.$scope.metric.metricHistory?.length;
    if (shouldCheckin) {
      const checkin: ICheckin = {
        checkInDate: new Date().toISOString(),
        actual: this.$scope.metric.actual,
        confidence: this.$scope.metric.confidence?.value,
        customFields: this.$scope.metric.customFields,
      };
      this.metricService.checkin({ id: this.$scope.metric.id }, checkin).then(() => {
        this.okrEvents.broadcastGoalsChanged("metricSnapshotCreated", {
          checkin,
          metric: this.$scope.metric,
          goal: this.$scope.goal,
          session: this.$scope.session,
        });
        patch(metric);
      });
    } else {
      patch(metric);
    }
  }

  private handleDynamicMEtricUpdated() {
    this.okrEvents.broadcastGoalsChanged("goalChanged");
    delete this.$scope.indicators.saving;
    this.$scope.$dismiss();
  }

  private setMetricCriticalTarget = (metric: Metric): void => {
    if (metric.targetOperator === "should_stay_above" || metric.targetOperator === "should_stay_below") {
      return;
    }

    // if metric is not anti-target KR --> set critical value to null
    this.$scope.metric.critical = null;
  };

  private validateInputValues = (targetOperator: MetricTargetOperator, targetValue: number, criticalValue: number, initialValue: number): boolean => {
    if (initialValue === null || targetValue === null) {
      this.$scope.errorMessage = null;

      return false;
    }

    if (targetOperator === "should_stay_above" && criticalValue !== null && typeof criticalValue !== "undefined" && criticalValue >= targetValue) {
      this.$scope.errorMessage = "critical_lower_than_target";
      this.$scope.errorMessageId = "critical-lower-than-target";

      return false;
    }

    if (targetOperator === "should_stay_below" && criticalValue !== null && typeof criticalValue !== "undefined" && criticalValue <= targetValue) {
      this.$scope.errorMessage = "critical_greater_than_target";
      this.$scope.errorMessageId = "critical_greater_than_target";

      return false;
    }

    if (targetOperator === "at_least" && initialValue >= targetValue) {
      this.$scope.errorMessage = "initial_lower_than_target";
      this.$scope.errorMessageId = "initial_lower_than_target";

      return false;
    }

    if (targetOperator === "at_most" && initialValue <= targetValue) {
      this.$scope.errorMessage = "initial_greater_than_target";
      this.$scope.errorMessageId = "initial_greater_than_target";

      return false;
    }

    return true;
  };

  private includeCritical = (params: { useCritical: boolean }): void => {
    if (params.useCritical === true) {
      // setting metric.critical to be null when critical target is not used
      this.$scope.metric.critical = null;

      this.$scope.useCritical = false;
    } else {
      // ng-init in this case is set by the attributes of the input field
      this.$scope.useCritical = true;
    }

    this.$scope.$evalAsync();
  };

  private onInsightSelectorChange = (): void => {
    this.$scope.metric = this.metricService.updateMetricInsightData(this.$scope.metric, this.$scope.insightSelector.data);
  };

  private updateCustomFields = (customFields: CfMap): void => {
    this.$scope.metric.customFields = customFields;
  };

  private setRequiredCf = (requiredCf: { areFilled: boolean }): void => {
    this.$scope.requiredCfNotEmpty = requiredCf.areFilled;

    this.$scope.$evalAsync();
  };

  private selectMultipleOwners = (ids: string[]): void => {
    if (!ids.length) {
      return;
    }

    this.$scope.metric.ownerIds = ids;
  };

  private updateMetricField = (updatedField: RequireAtLeastOne<Metric>) => {
    if (updatedField.milestones?.length > 0) {
      this.setLastMilestone(updatedField.milestones[0]);

      updatedField.milestones = updatedField.milestones.filter((milestone) => {
        return milestone.targetValue !== null && typeof milestone.targetValue !== "undefined" && milestone.date !== null && typeof milestone.date !== "undefined";
      });
    }

    if (updatedField.target || updatedField.targetOperator) {
      this.validateMilestoneValue(updatedField.target || this.$scope.metric.target, updatedField.targetOperator || this.$scope.metric.targetOperator);
    }

    this.$scope.metric = { ...this.$scope.metric, ...updatedField };
    this.$scope.$evalAsync();
  };

  private setLastMilestone = (milestone: Milestone): void => {
    if (!milestone?.targetValue && !milestone?.date) {
      this.$scope.lastMilestone = null;
      return;
    }

    this.$scope.lastMilestone = milestone;
  };

  private validateMilestoneValue = (target: number, targetOperator: MetricTargetOperator): void => {
    const targetValue = this.$scope.lastMilestone?.targetValue;

    if (!targetValue) {
      return;
    }

    if (targetOperator === "at_least" && targetValue > target) {
      this.$scope.milestoneErrorMessageId = "milestone_greater_than_target";
      return;
    }

    if (targetOperator === "at_most" && targetValue < target) {
      this.$scope.milestoneErrorMessageId = "milestone_less_than_target";
      return;
    }

    this.$scope.milestoneErrorMessageId = null;
  };

  private setDueDate = (dueDate: string, type: MetricDeadLineType): void => {
    switch (type) {
      case "softDeadline":
        this.$scope.dates.softDueDate = dueDate;
        break;
      case "hardDeadline":
        this.$scope.dates.dueDate = dueDate;
        break;
    }
    this.$scope.$evalAsync();
  };

  private setPeopleSelectorRequest(): void {
    this.$scope.peopleSelectorRequest = {
      ...new PeopleSelectorRequest(),
      type: this.$scope.methodologySettings.assignMetricPermissions,
      permissionSetting: { enabled: true, permission: "ManageGoals" },
    };
  }

  private filterBySessionId = (customFields: ICustomField[]): ICustomField[] => {
    return filterCustomFieldsBySession(this.$scope.goal.sessionId, customFields);
  };

  private isCustomFieldDisabled = (): boolean => {
    return this.$scope.goal.locked;
  };

  private closeModal = ($event?: KeyboardEvent): void => {
    if (!$event || $event.key === "Enter") {
      this.$scope.$dismiss();
    }
  };
}
